// 管理者ログイン(設計書 §3.5.1)
// email + password + TOTP 6 桁を 1 リクエストで送信。

import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiClientError } from '../../api/client';
import { useAdminAuth } from '../../auth/AdminAuthContext';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/ToastContainer';
import { consumeExpiredFlash } from '../../lib/auth-events';

interface LocationState {
  from?: { pathname?: string };
}

export function AdminLoginPage() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // セッション期限切れ flash → トースト 1 回。scope='admin' のものだけ消化し、
  // performer scope のフラッシュは /login に温存する。
  useEffect(() => {
    if (consumeExpiredFlash('admin') === 'admin') {
      showToast(
        'セッションの有効期限が切れました。再度ログインしてください。',
        { level: 'warn' },
      );
    }
  }, [showToast]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(totpCode)) {
      setError('TOTP コードは 6 桁の数字で入力してください');
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password, totpCode);
      const state = location.state as LocationState | null;
      const target = state?.from?.pathname && state.from.pathname.startsWith('/admin')
        ? state.from.pathname
        : '/admin/dashboard';
      navigate(target, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'ログインに失敗しました。',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="管理者ログイン" subtitle="email + パスワード + TOTP コード">
      <form onSubmit={handleSubmit}>
        <Input
          label="メールアドレス"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <Input
          label="パスワード"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <Input
          label="TOTP コード(6 桁)"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
          autoComplete="one-time-code"
          required
        />
        {error && (
          <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--color-error-text)' }}>
            {error}
          </p>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'ログイン中…' : 'ログイン'}
        </Button>
      </form>
    </AuthLayout>
  );
}
